import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { User, CreditCard, Link2, Zap, ShieldCheck, ShieldOff, Package, Plus, Trash2, Star } from 'lucide-react';
import { Check, X, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import { Platform, PLATFORM_NAMES } from '@crosova/shared';
import { useExtensionDetected, isExtensionPlatform } from '../../lib/extension';

type Tab = 'profile' | 'billing' | 'platforms' | 'shipping' | 'automation';

const VALID_TABS: Tab[] = ['profile', 'billing', 'platforms', 'shipping', 'automation'];

const TABS: { id: Tab; label: string; icon: any }[] = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'billing', label: 'Billing', icon: CreditCard },
  { id: 'platforms', label: 'Platforms', icon: Link2 },
  { id: 'shipping', label: 'Shipping', icon: Package },
  { id: 'automation', label: 'Automation', icon: Zap },
];

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040', facebook: '#1877f2',
  grailed: '#1a1a1a', mercari: '#ff5722', poshmark: '#7b1fa2', shopify: '#95bf47', whatnot: '#ffc940',
  bigcommerce: '#34313f', woocommerce: '#96588a',
};

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as Tab | null;
  const tab: Tab = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'profile';

  function setTab(t: Tab) {
    setSearchParams({ tab: t }, { replace: true });
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div>
        <h1 className="text-base font-semibold tracking-tight">Settings</h1>
        <p className="text-[11.5px] text-white/25 mt-0.5">Manage your account, billing, and integrations</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <aside className="w-full md:w-[180px] flex-shrink-0">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] mb-0.5 transition-colors ${
                  tab === t.id ? 'bg-primary-500/[0.15] text-primary-300' : 'text-white/40 hover:bg-white/[0.03]'
                }`}
              >
                <Icon size={14} />
                {t.label}
              </button>
            );
          })}
        </aside>

        <div className="flex-1 min-w-0">
          {tab === 'profile' && <ProfileTab />}
          {tab === 'billing' && <BillingTab />}
          {tab === 'platforms' && <PlatformsTab />}
          {tab === 'shipping' && <ShippingTab />}
          {tab === 'automation' && <AutomationTab />}
        </div>
      </div>
    </div>
  );
}

function ProfileTab() {
  const qc = useQueryClient();
  const nameRef = useRef<HTMLInputElement>(null);
  const [saved, setSaved] = useState(false);

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me').then((r) => r.data.user),
  });

  const update = useMutation({
    mutationFn: (name: string) => api.patch('/auth/me', { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  return (
    <div className="space-y-3">
      <div className="card p-5 space-y-4">
        <h2 className="text-sm font-semibold">Account</h2>
        <Field label="Name">
          <input ref={nameRef} className="input" defaultValue={me?.name} />
        </Field>
        <Field label="Email">
          <input className="input" defaultValue={me?.email} disabled />
        </Field>
        <div className="flex items-center gap-3">
          <button
            onClick={() => nameRef.current && update.mutate(nameRef.current.value)}
            disabled={update.isPending}
            className="btn-primary"
          >
            {update.isPending ? 'Saving...' : 'Save changes'}
          </button>
          {saved && <span className="text-[11px] text-success">Saved!</span>}
          {update.isError && <span className="text-[11px] text-danger">Failed to save</span>}
        </div>
        <div className="border-t border-white/[0.06] pt-4">
          <button
            onClick={() => api.post('/auth/logout').then(() => { window.location.href = '/login'; })}
            className="text-[12px] text-danger hover:underline"
          >
            Log out
          </button>
        </div>
      </div>

      <TwoFactorPanel enabled={me?.twoFactorEnabled ?? false} method={me?.twoFactorMethod ?? 'email'} />
    </div>
  );
}

type TfaStep = 'idle' | 'pick-method' | 'setup-email' | 'setup-totp' | 'backup-codes' | 'disable';

function TwoFactorPanel({ enabled, method }: { enabled: boolean; method: string }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<TfaStep>('idle');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent] = useState(false);

  async function pickMethod(chosen: 'email' | 'totp') {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/2fa/setup', { method: chosen });
      setBackupCodes(data.backupCodes);
      if (chosen === 'totp') {
        setQrCode(data.qrCodeDataUrl);
        setSecret(data.secret);
        setStep('setup-totp');
      } else {
        setStep('setup-email');
      }
    } catch {
      setError('Failed to start setup. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function confirmEnable() {
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/2fa/enable', { code });
      qc.invalidateQueries({ queryKey: ['me'] });
      setStep('backup-codes');
      setCode('');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Invalid code. Try again.');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  async function sendDisableCode() {
    setLoading(true);
    try {
      await api.post('/auth/2fa/disable-send');
      setResent(true);
    } catch {
      setError('Failed to send code');
    } finally {
      setLoading(false);
    }
  }

  async function confirmDisable() {
    if (code.length !== 6) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/2fa/disable', { code });
      qc.invalidateQueries({ queryKey: ['me'] });
      setStep('idle');
      setCode('');
      setResent(false);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Invalid code');
      setCode('');
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setStep('idle');
    setCode('');
    setError('');
    setQrCode('');
    setSecret('');
    setResent(false);
  }

  const methodLabel = method === 'email' ? 'email code' : 'authenticator app';

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {enabled
            ? <ShieldCheck size={15} className="text-secondary-400" />
            : <ShieldOff size={15} className="text-white/30" />}
          <h2 className="text-sm font-semibold">Two-factor authentication</h2>
        </div>
        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${enabled ? 'bg-secondary-400/10 text-secondary-400' : 'bg-white/[0.06] text-white/30'}`}>
          {enabled ? `On · ${methodLabel}` : 'Off'}
        </span>
      </div>

      {step === 'idle' && (
        <>
          <p className="text-[12px] text-white/40 mb-4">
            {enabled
              ? `Your account requires a verification code at each login via ${methodLabel}.`
              : 'Protect your account with a verification code every time you log in.'}
          </p>
          {enabled ? (
            <button onClick={() => { setStep('disable'); if (method === 'email') sendDisableCode(); }} className="btn-secondary text-[12px] flex items-center gap-1.5">
              <ShieldOff size={13} /> Disable 2FA
            </button>
          ) : (
            <button onClick={() => setStep('pick-method')} className="btn-primary text-[12px]">
              Enable 2FA
            </button>
          )}
        </>
      )}

      {step === 'pick-method' && (
        <div className="space-y-3">
          <p className="text-[12px] text-white/50 mb-3">How would you like to receive your verification code?</p>
          <button
            onClick={() => pickMethod('email')}
            disabled={loading}
            className="w-full text-left card p-4 hover:bg-white/[0.05] transition-colors space-y-0.5"
          >
            <div className="text-[13px] font-medium">Email code</div>
            <div className="text-[11.5px] text-white/40">We'll send a 6-digit code to your email each time you log in. No app needed.</div>
          </button>
          <button
            onClick={() => pickMethod('totp')}
            disabled={loading}
            className="w-full text-left card p-4 hover:bg-white/[0.05] transition-colors space-y-0.5"
          >
            <div className="text-[13px] font-medium">Authenticator app</div>
            <div className="text-[11.5px] text-white/40">Use Google Authenticator, Authy, or any TOTP app. Works offline.</div>
          </button>
          {error && <p className="text-[11px] text-orange-400">{error}</p>}
          <button onClick={reset} className="text-[12px] text-white/30 hover:text-white/60 transition-colors">Cancel</button>
        </div>
      )}

      {step === 'setup-email' && (
        <div className="space-y-4">
          <p className="text-[12px] text-white/50">
            We've sent a verification code to your email. Enter it below to confirm and activate 2FA.
          </p>
          <input
            className="input text-center tracking-[0.3em] text-lg font-mono"
            placeholder="000000"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
          />
          {error && <p className="text-[11px] text-orange-400">{error}</p>}
          <div className="flex gap-2">
            <button onClick={confirmEnable} disabled={loading || code.length !== 6} className="btn-primary flex-1 text-[12px]">
              {loading ? 'Verifying...' : 'Confirm & enable'}
            </button>
            <button onClick={reset} className="btn-secondary text-[12px]">Cancel</button>
          </div>
        </div>
      )}

      {step === 'setup-totp' && (
        <div className="space-y-4">
          <p className="text-[12px] text-white/50">
            Scan this QR code with your authenticator app, then enter the 6-digit code to confirm.
          </p>
          <div className="flex justify-center">
            <img src={qrCode} alt="2FA QR code" className="w-44 h-44 rounded-lg bg-white p-1" />
          </div>
          <div>
            <p className="text-[11px] text-white/30 mb-1">Can't scan? Enter this key manually:</p>
            <code className="text-[11px] bg-white/[0.06] px-2 py-1 rounded font-mono tracking-widest block text-center">{secret}</code>
          </div>
          <input
            className="input text-center tracking-[0.3em] text-lg font-mono"
            placeholder="000000"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          {error && <p className="text-[11px] text-orange-400">{error}</p>}
          <div className="flex gap-2">
            <button onClick={confirmEnable} disabled={loading || code.length !== 6} className="btn-primary flex-1 text-[12px]">
              {loading ? 'Verifying...' : 'Confirm & enable'}
            </button>
            <button onClick={reset} className="btn-secondary text-[12px]">Cancel</button>
          </div>
        </div>
      )}

      {step === 'backup-codes' && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <span className="text-amber-400 text-xs mt-0.5">⚠</span>
            <p className="text-[11.5px] text-amber-300">
              Save these backup codes somewhere safe. Each works once if you ever lose access to your verification method.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {backupCodes.map((c) => (
              <code key={c} className="text-[12px] bg-white/[0.06] px-3 py-1.5 rounded font-mono text-center tracking-widest">{c}</code>
            ))}
          </div>
          <button onClick={reset} className="btn-primary w-full text-[12px]">Done — I've saved my codes</button>
        </div>
      )}

      {step === 'disable' && (
        <div className="space-y-4">
          <p className="text-[12px] text-white/50">
            {method === 'email'
              ? 'Enter the code we just sent to your email to disable 2FA.'
              : 'Enter your current authenticator code to disable 2FA.'}
          </p>
          <input
            className="input text-center tracking-[0.3em] text-lg font-mono"
            placeholder="000000"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            autoFocus
          />
          {method === 'email' && (
            <button type="button" onClick={sendDisableCode} disabled={loading} className="text-[11px] text-white/30 hover:text-white/60 transition-colors block">
              {resent ? 'Code resent!' : 'Resend code'}
            </button>
          )}
          {error && <p className="text-[11px] text-orange-400">{error}</p>}
          <div className="flex gap-2">
            <button onClick={confirmDisable} disabled={loading || code.length !== 6} className="btn-primary flex-1 text-[12px] bg-danger hover:bg-red-600">
              {loading ? 'Disabling...' : 'Disable 2FA'}
            </button>
            <button onClick={reset} className="btn-secondary text-[12px]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function BillingTab() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.get('/auth/me').then((r) => r.data.user) });

  async function startCheckout(plan: 'starter' | 'pro') {
    const { data } = await api.post('/billing/checkout', { plan });
    if (data.url) window.location.href = data.url;
  }

  async function openPortal() {
    const { data } = await api.post('/billing/portal');
    if (data.url) window.location.href = data.url;
  }

  return (
    <div className="space-y-3">
      <div className="card p-5">
        <h2 className="text-sm font-semibold mb-3">Current plan</h2>
        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-semibold capitalize">{me?.plan ?? 'trial'}</div>
          {me?.plan === 'trial' && me?.trialEndsAt && (
            <div className="text-[11px] text-secondary-400">Trial ends {new Date(me.trialEndsAt).toLocaleDateString()}</div>
          )}
        </div>
        {me?.plan !== 'trial' && (
          <button onClick={openPortal} className="btn-secondary mt-4">Manage subscription</button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <PlanCard
          name="Starter"
          price="£9.99"
          features={['200 listings/mo', '5 platforms', 'Basic AI assistant', 'Email support']}
          onChoose={() => startCheckout('starter')}
        />
        <PlanCard
          name="Pro"
          price="£24.99"
          recommended
          features={['Unlimited listings', 'All 10 platforms', 'Full AI suite', 'Auto-delist on sale', 'Price advisor', 'Priority support']}
          onChoose={() => startCheckout('pro')}
        />
      </div>
    </div>
  );
}

function PlanCard({ name, price, features, recommended, onChoose }: { name: string; price: string; features: string[]; recommended?: boolean; onChoose: () => void }) {
  return (
    <div className={`card p-5 relative ${recommended ? 'border-primary-500/30' : ''}`}>
      {recommended && <div className="absolute -top-2 left-4 bg-primary-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded">RECOMMENDED</div>}
      <div className="text-sm text-white/40">{name}</div>
      <div className="text-2xl font-semibold mt-1">{price}<span className="text-xs text-white/40 font-normal">/month</span></div>
      <ul className="mt-4 space-y-1.5 text-[11.5px]">
        {features.map((f) => (
          <li key={f} className="flex items-center gap-2 text-white/60">
            <Check size={12} className="text-secondary-400 flex-shrink-0" /> {f}
          </li>
        ))}
      </ul>
      <button onClick={onChoose} className={`w-full mt-4 ${recommended ? 'btn-primary' : 'btn-secondary'}`}>Choose {name}</button>
    </div>
  );
}

// Platforms that use direct credentials instead of OAuth
const DIRECT_PLATFORMS: Partial<Record<Platform, { fields: { key: string; label: string; placeholder: string; type?: string }[]; note?: string }>> = {
  // Vinted and Depop use the Chrome extension — no credentials needed
  [Platform.BIGCOMMERCE]: {
    fields: [
      { key: 'storeHash', label: 'Store Hash', placeholder: 'abc123xyz (from your BigCommerce URL)' },
      { key: 'apiKey', label: 'API Key', placeholder: 'Your BigCommerce API key', type: 'password' },
    ],
  },
  [Platform.WOOCOMMERCE]: {
    fields: [
      { key: 'storeUrl', label: 'Store URL', placeholder: 'https://yourstore.com' },
      { key: 'consumerKey', label: 'Consumer Key', placeholder: 'ck_...' },
      { key: 'consumerSecret', label: 'Consumer Secret', placeholder: 'cs_...', type: 'password' },
    ],
  },
};

function PlatformsTab() {
  const qc = useQueryClient();
  const extensionDetected = useExtensionDetected();
  const [directForm, setDirectForm] = useState<{ platform: Platform; values: Record<string, string> } | null>(null);
  const [msg, setMsg] = useState<{ text: string; type: 'info' | 'error' } | null>(null);

  const { data: platforms = [] } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.get('/platforms').then((r) => r.data),
  });

  const connect = useMutation({
    mutationFn: (platform: Platform) => api.post(`/platforms/${platform}/connect`).then((r) => r.data),
    onSuccess: (data, platform) => {
      if (data.status === 'direct') {
        setDirectForm({ platform, values: {} });
        setMsg(null);
      } else if (data.authUrl) {
        window.location.href = data.authUrl;
      } else if (data.status === 'browser') {
        setMsg({ type: 'info', text: 'Vinted uses browser automation to list items on your behalf. Browser-based connections are launching soon — we\'ll notify you when it\'s ready.' });
      } else if (data.status === 'coming_soon') {
        setMsg({ type: 'info', text: `${PLATFORM_NAMES[platform]} is coming soon. We'll notify you when this platform is available to connect.` });
      }
    },
    onError: () => {
      setMsg({ type: 'error', text: 'Failed to initiate connection. Please try again.' });
    },
  });

  const saveCredentials = useMutation({
    mutationFn: ({ platform, values }: { platform: Platform; values: Record<string, string> }) =>
      api.post(`/platforms/${platform}/credentials`, values),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['platforms'] }); setDirectForm(null); },
  });

  const disconnect = useMutation({
    mutationFn: (platform: Platform) => api.post(`/platforms/${platform}/disconnect`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['platforms'] }),
  });

  return (
    <div className="space-y-3">
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.05]">
          <h2 className="text-sm font-semibold">Marketplaces</h2>
          <p className="text-[11px] text-white/30 mt-0.5">Connect your existing seller accounts</p>
        </div>

        {msg && (
          <div className={`mx-4 mt-3 mb-1 flex items-start gap-2 rounded-lg px-3 py-2 text-[11.5px] ${msg.type === 'error' ? 'bg-red-500/10 border border-red-500/20 text-red-300' : 'bg-blue-500/10 border border-blue-500/20 text-blue-200'}`}>
            <span className="flex-1">{msg.text}</span>
            <button onClick={() => setMsg(null)} className="text-white/30 hover:text-white/60 transition-colors flex-shrink-0">
              <X size={13} />
            </button>
          </div>
        )}

        <div className="divide-y divide-white/[0.04]">
          {Object.values(Platform).map((p) => {
            const account = platforms.find((a: any) => a.platform === p);
            const connected = account?.connected;
            const isDirect = !!DIRECT_PLATFORMS[p];
            const isExt = isExtensionPlatform(p);
            return (
              <div key={p} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs text-white" style={{ background: PLATFORM_COLOURS[p] ?? '#555' }}>
                    {PLATFORM_NAMES[p].slice(0, 2)}
                  </div>
                  <div>
                    <div className="text-[13px] font-medium">{PLATFORM_NAMES[p]}</div>
                    <div className="text-[11px] text-white/30">
                      {isExt
                        ? (extensionDetected ? 'Extension detected' : 'Requires Crosova Extension')
                        : connected ? 'Connected' : isDirect ? 'API key connection' : 'OAuth connection'}
                    </div>
                  </div>
                </div>
                {isExt ? (
                  extensionDetected
                    ? <span className="text-[11px] text-emerald-400">Ready</span>
                    : <span className="text-[11px] text-orange-400">Install extension</span>
                ) : connected ? (
                  <button onClick={() => disconnect.mutate(p)} className="text-[11px] text-orange-400 hover:underline">Disconnect</button>
                ) : (
                  <button onClick={() => connect.mutate(p)} className="btn-secondary !py-1 !px-3 !text-[11px]">Connect</button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Credentials form for direct-connect platforms */}
      {directForm && DIRECT_PLATFORMS[directForm.platform] && (
        <div className="card p-5 space-y-3 border-primary-500/20">
          <h3 className="text-[13px] font-semibold">Connect {PLATFORM_NAMES[directForm.platform]}</h3>
          {DIRECT_PLATFORMS[directForm.platform]!.note && (
            <p className="text-[11.5px] text-white/40">{DIRECT_PLATFORMS[directForm.platform]!.note}</p>
          )}
          {DIRECT_PLATFORMS[directForm.platform]!.fields.map((field) => (
            <div key={field.key}>
              <label className="text-[10px] text-white/35 block mb-1">{field.label}</label>
              <input
                className="input"
                type={field.type ?? 'text'}
                placeholder={field.placeholder}
                value={directForm.values[field.key] ?? ''}
                onChange={(e) => setDirectForm((f) => f ? { ...f, values: { ...f.values, [field.key]: e.target.value } } : null)}
              />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => saveCredentials.mutate({ platform: directForm.platform, values: directForm.values })}
              disabled={saveCredentials.isPending}
              className="btn-primary text-[12px]"
            >
              {saveCredentials.isPending ? 'Connecting...' : 'Save & connect'}
            </button>
            <button onClick={() => setDirectForm(null)} className="btn-secondary text-[12px]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ShippingTab() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState({
    name: '', shippingService: 'Royal Mail 2nd Class',
    shippingCostPence: 0, freeShipping: false,
    dispatchDays: 1, returnsAccepted: true, returnDays: 30,
    returnsPaidBy: 'buyer', isDefault: false,
  });

  const { data: policies = [] } = useQuery({
    queryKey: ['policies'],
    queryFn: () => api.get('/policies').then((r) => r.data),
  });

  const save = useMutation({
    mutationFn: () => editing
      ? api.patch(`/policies/${editing.id}`, form)
      : api.post('/policies', form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['policies'] }); setShowForm(false); setEditing(null); },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/policies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });

  function startEdit(p: any) {
    setForm({ name: p.name, shippingService: p.shippingService, shippingCostPence: p.shippingCostPence,
      freeShipping: p.freeShipping, dispatchDays: p.dispatchDays, returnsAccepted: p.returnsAccepted,
      returnDays: p.returnDays, returnsPaidBy: p.returnsPaidBy, isDefault: p.isDefault });
    setEditing(p);
    setShowForm(true);
  }

  function startNew() {
    setForm({ name: '', shippingService: 'Royal Mail 2nd Class', shippingCostPence: 0, freeShipping: false,
      dispatchDays: 1, returnsAccepted: true, returnDays: 30, returnsPaidBy: 'buyer', isDefault: false });
    setEditing(null);
    setShowForm(true);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Shipping policies</h2>
          <p className="text-[11.5px] text-white/30 mt-0.5">Reusable shipping & returns settings for your listings</p>
        </div>
        <button onClick={startNew} className="btn-primary text-[12px] flex items-center gap-1.5">
          <Plus size={13} /> New policy
        </button>
      </div>

      {policies.length === 0 && !showForm && (
        <div className="card p-8 text-center">
          <Package size={28} className="text-white/10 mx-auto mb-3" />
          <p className="text-sm text-white/30">No shipping policies yet</p>
          <p className="text-[11px] text-white/20 mt-1">Create one to apply it quickly when listing items</p>
        </div>
      )}

      {policies.map((p: any) => (
        <div key={p.id} className="card p-4 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-[13px] font-medium">{p.name}</span>
              {p.isDefault && <Star size={11} className="text-amber-400 fill-amber-400" />}
            </div>
            <div className="text-[11.5px] text-white/40">
              {p.freeShipping ? 'Free shipping' : `£${(p.shippingCostPence / 100).toFixed(2)} — ${p.shippingService}`}
              {' · '}{p.dispatchDays}d dispatch
              {p.returnsAccepted ? ` · ${p.returnDays}-day returns (paid by ${p.returnsPaidBy})` : ' · No returns'}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => startEdit(p)} className="btn-secondary text-[11px] !py-1">Edit</button>
            <button onClick={() => remove.mutate(p.id)} className="text-white/20 hover:text-danger transition-colors">
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      ))}

      {showForm && (
        <div className="card p-5 space-y-3 border-primary-500/20">
          <h3 className="text-[13px] font-semibold">{editing ? 'Edit policy' : 'New policy'}</h3>
          <div>
            <label className="text-[10px] text-white/35 block mb-1">Policy name</label>
            <input className="input" placeholder="e.g. Royal Mail 2nd Class" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-white/35 block mb-1">Shipping service</label>
              <input className="input" value={form.shippingService}
                onChange={(e) => setForm((f) => ({ ...f, shippingService: e.target.value }))} />
            </div>
            <div>
              <label className="text-[10px] text-white/35 block mb-1">Shipping cost (£)</label>
              <input className="input" type="number" min="0" step="0.01"
                value={(form.shippingCostPence / 100).toFixed(2)}
                onChange={(e) => setForm((f) => ({ ...f, shippingCostPence: Math.round(parseFloat(e.target.value || '0') * 100) }))} />
            </div>
            <div>
              <label className="text-[10px] text-white/35 block mb-1">Dispatch within (days)</label>
              <input className="input" type="number" min="0" max="30" value={form.dispatchDays}
                onChange={(e) => setForm((f) => ({ ...f, dispatchDays: parseInt(e.target.value) || 1 }))} />
            </div>
            <div>
              <label className="text-[10px] text-white/35 block mb-1">Return window (days)</label>
              <input className="input" type="number" min="0" max="60" value={form.returnDays}
                onChange={(e) => setForm((f) => ({ ...f, returnDays: parseInt(e.target.value) || 30 }))} />
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={form.freeShipping}
                onChange={(e) => setForm((f) => ({ ...f, freeShipping: e.target.checked }))} />
              Free shipping
            </label>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={form.returnsAccepted}
                onChange={(e) => setForm((f) => ({ ...f, returnsAccepted: e.target.checked }))} />
              Accept returns
            </label>
            <label className="flex items-center gap-2 text-[12px] cursor-pointer">
              <input type="checkbox" checked={form.isDefault}
                onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))} />
              <Star size={11} className="text-amber-400" /> Set as default
            </label>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={() => save.mutate()} disabled={!form.name || save.isPending} className="btn-primary text-[12px]">
              {save.isPending ? 'Saving...' : 'Save policy'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary text-[12px]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AutomationTab() {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.get('/auth/me').then((r) => r.data.user) });

  const update = useMutation({
    mutationFn: (data: object) => api.patch('/auth/automation-settings', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });

  function set(field: string, value: boolean | number) {
    update.mutate({ [field]: value });
  }

  return (
    <div className="space-y-3">
      <div className="card p-5 space-y-4">
        <h2 className="text-sm font-semibold">General</h2>

        {/* Auto-delist — real setting wired to API */}
        <div className="flex items-start justify-between gap-4 py-2 border-b border-white/[0.04]">
          <div>
            <div className="text-[13px] font-medium">Auto-delist on sale</div>
            <div className="text-[11px] text-white/30 mt-0.5">When an item sells on one platform, automatically remove it from all others — prevents double-selling</div>
          </div>
          <button
            onClick={() => set('autoDelistEnabled', !me?.autoDelistEnabled)}
            className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${me?.autoDelistEnabled !== false ? 'bg-primary-500' : 'bg-white/10'}`}
          >
            <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all ${me?.autoDelistEnabled !== false ? 'left-[19px]' : 'left-[3px]'}`} />
          </button>
        </div>

        {/* Coming soon items — clearly labelled, not fake toggles */}
        {[
          { label: 'Price drop reminders', desc: 'Get a nudge when similar items are selling for less — so you can reprice to stay competitive' },
          { label: 'Smart scheduling', desc: 'Automatically publish listings at peak browsing times for each marketplace' },
        ].map((item) => (
          <div key={item.label} className="flex items-start justify-between gap-4 py-2 border-b border-white/[0.04] last:border-0 opacity-50">
            <div>
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-medium">{item.label}</div>
                <span className="text-[9.5px] bg-white/[0.06] text-white/40 px-1.5 py-0.5 rounded">Coming soon</span>
              </div>
              <div className="text-[11px] text-white/30 mt-0.5">{item.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-sm font-semibold">Auto offers to eBay watchers</h2>
            <p className="text-[11.5px] text-white/30 mt-0.5">
              Automatically send a discount offer to people who watch your eBay listings
            </p>
          </div>
          <button
            onClick={() => set('autoOffersEnabled', !me?.autoOffersEnabled)}
            className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${me?.autoOffersEnabled ? 'bg-primary-500' : 'bg-white/10'}`}
          >
            <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all ${me?.autoOffersEnabled ? 'left-[19px]' : 'left-[3px]'}`} />
          </button>
        </div>

        {me?.autoOffersEnabled && (
          <div className="space-y-3 border-t border-white/[0.05] pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-white/35 block mb-1">Discount %</label>
                <input
                  className="input"
                  type="number" min="1" max="50"
                  value={me?.autoOfferDiscountPct ?? 10}
                  onChange={(e) => set('autoOfferDiscountPct', parseInt(e.target.value) || 10)}
                />
              </div>
              <div>
                <label className="text-[10px] text-white/35 block mb-1">Min listing price (£)</label>
                <input
                  className="input"
                  type="number" min="0" step="0.01"
                  value={((me?.autoOfferMinPricePence ?? 500) / 100).toFixed(2)}
                  onChange={(e) => set('autoOfferMinPricePence', Math.round(parseFloat(e.target.value || '0') * 100))}
                />
              </div>
              <div>
                <label className="text-[10px] text-white/35 block mb-1">Offer expires (days)</label>
                <input
                  className="input"
                  type="number" min="1" max="7"
                  value={me?.autoOfferExpiryDays ?? 3}
                  onChange={(e) => set('autoOfferExpiryDays', parseInt(e.target.value) || 3)}
                />
              </div>
            </div>
            <div className="flex items-start gap-2 bg-amber-500/[0.07] border border-amber-500/20 rounded-lg px-3 py-2">
              <span className="text-amber-400 text-xs mt-0.5">⚠</span>
              <p className="text-[11px] text-amber-300">
                Auto offers require the <strong>sell.negotiation</strong> eBay permission.
                If you connected eBay before this feature was added, reconnect eBay in the Platforms tab.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, desc, defaultChecked }: { label: string; desc: string; defaultChecked?: boolean }) {
  const [on, setOn] = useState(defaultChecked ?? false);
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-white/[0.04] last:border-0">
      <div>
        <div className="text-[13px] font-medium">{label}</div>
        <div className="text-[11px] text-white/30 mt-0.5">{desc}</div>
      </div>
      <button onClick={() => setOn(!on)} className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 ${on ? 'bg-primary-500' : 'bg-white/10'}`}>
        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all ${on ? 'left-[19px]' : 'left-[3px]'}`} />
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] text-white/35 mb-1">{label}</div>
      {children}
    </div>
  );
}
